import { Link, useParams } from "react-router-dom";
import { Check, ArrowRight, MessageCircle, X } from "lucide-react";
import Seo, { crumbsLd, faqLd } from "@/components/site/Seo";
import { PageHero, FaqList, SectionHead } from "@/components/site/Blocks";
import PricingCards, { PackageCard } from "@/components/site/PricingCards";
import BeforeAfter from "@/components/site/BeforeAfter";
import { CtaBand } from "@/components/site/Cta";
import { getService, SERVICES } from "@/data/services";
import { CRM_PACKAGES, SOCIAL_PACKAGES, SOCIAL_EXCLUSIONS, SOCIAL_TERMS, VAT_NOTE } from "@/data/pricing";
import { waLink } from "@/data/site";
import NotFound from "./NotFound";
import { Instagram, Facebook, Shopify, WooCommerce, GoogleG, GAnalytics, GSearchConsole, WhatsApp } from "@/components/site/Brands";

const BRANDS = {
  "gestione-social-media": [[<Instagram key="i" size="h-9 w-9" icon="h-5 w-5" />, "Instagram"], [<Facebook key="f" size="h-9 w-9" icon="h-5 w-5" />, "Facebook"]],
  "e-commerce": [[<Shopify key="s" className="h-6 w-6" />, "Shopify"], [<WooCommerce key="w" className="h-7 w-7" />, "WooCommerce"]],
  "seo-visibilita-online": [[<GoogleG key="g" className="h-6 w-6" />, "Ricerca Google"], [<GSearchConsole key="c" className="h-6 w-6" />, "Search Console"], [<GAnalytics key="a" className="h-6 w-6" />, "Analytics"]],
  "cataloghi-prodotti-online": [[<WhatsApp key="wa" size="h-9 w-9" icon="h-5 w-5" />, "Richieste su WhatsApp"]],
};
const BRAND_NOTE = { "e-commerce": "La piattaforma viene valutata in base al progetto." };

const BrandRow = ({ slug }) => BRANDS[slug] ? (
  <div data-testid="service-brands" className="flex w-full flex-wrap items-center gap-3 pt-2">
    {BRANDS[slug].map(([el, l]) => <span key={l} className="flex items-center gap-2 rounded-2xl border border-white/15 bg-white px-3 py-2 text-sm font-semibold text-slate-900">{el}{l}</span>)}
    {BRAND_NOTE[slug] && <span className="text-xs text-slate-600">{BRAND_NOTE[slug]}</span>}
  </div>
) : null;

export const Sections = ({ sections }) => sections.map((s) => (
  <div key={s.h2} className="border-t border-slate-100 py-12 first:border-t-0 first:pt-0">
    <h2 className="text-2xl font-bold sm:text-3xl">{s.h2}</h2>
    {s.body && <p className="mt-4 max-w-3xl text-base leading-relaxed text-slate-600 md:text-lg">{s.body}</p>}
    {s.bullets && (
      <ul className="mt-6 grid gap-3 sm:grid-cols-2">
        {s.bullets.map((b) => <li key={b} className="flex gap-3 rounded-xl bg-slate-50 p-4 text-[15px]"><Check className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />{b}</li>)}
      </ul>
    )}
  </div>
));

const SpecialPackages = ({ s }) => {
  if (s.crm) return <div className="grid gap-5 md:grid-cols-2">{CRM_PACKAGES.map((p) => <PackageCard key={p.id} p={p} />)}</div>;
  if (s.social) return (
    <>
      <div className="grid gap-5 md:grid-cols-2">{SOCIAL_PACKAGES.map((p) => <PackageCard key={p.id} p={p} />)}</div>
      <p className="mt-6 text-sm text-slate-600">{SOCIAL_TERMS}</p>
      <ul className="mt-4 flex flex-wrap gap-2">{SOCIAL_EXCLUSIONS.map((e) => <li key={e} className="flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-600"><X className="h-3 w-3" />{e}</li>)}</ul>
    </>
  );
  if (s.packages.length) return <PricingCards ids={s.packages} />;
  return (
    <div className="card flex flex-col items-start gap-4 p-8">
      <p className="font-display text-2xl font-extrabold">Su preventivo</p>
      <p className="text-slate-600">Indicativamente a partire da 2.500 € + IVA per progetti semplici, da valutare in base alle funzionalità.</p>
      <Link to="/preventivo-gratuito" className="btn-primary">Richiedi un preventivo <ArrowRight className="h-4 w-4" /></Link>
    </div>
  );
};

export default function ServicePage() {
  const { slug } = useParams();
  const s = getService(slug);
  if (!s) return <NotFound />;
  const crumbs = [{ label: "Home", to: "/" }, { label: "Servizi", to: "/servizi" }, { label: s.name, to: `/servizi/${s.slug}` }];
  const related = SERVICES.filter((x) => x.main && x.slug !== s.slug).slice(0, 4);
  return (
    <>
      <Seo path={`/servizi/${s.slug}`} title={s.title} description={s.description}
        jsonLd={[crumbsLd(crumbs), faqLd(s.faqs), { "@context": "https://schema.org", "@type": "Service", name: s.name, provider: { "@type": "Organization", name: "STUDIO WEB NS" }, areaServed: "IT", description: s.description }]} />
      <PageHero crumbs={crumbs} eyebrow={s.name} title={s.h1} intro={s.intro}>
        <Link to="/consulenza-online" data-testid="service-consult-btn" className="btn-primary">Prenota una consulenza online <ArrowRight className="h-4 w-4" /></Link>
        <a href={waLink(s.wa)} target="_blank" rel="noopener noreferrer" data-testid="service-whatsapp-btn" className="btn-ghost"><MessageCircle className="h-4 w-4" />Scrivici su WhatsApp</a>
        <BrandRow slug={s.slug} />
      </PageHero>
      <section className="py-14 md:py-20">
        <div className="wrap">
          <Sections sections={s.sections} />
          {s.beforeAfter && <BeforeAfter />}
        </div>
      </section>
      <section className="bg-soft py-14 md:py-20">
        <div className="wrap">
          <SectionHead eyebrow="Prezzi" title={s.crm ? "Pacchetti CRM" : s.social ? "Pacchetti social" : "Pacchetti consigliati"} intro={VAT_NOTE} />
          <div className="mt-12"><SpecialPackages s={s} /></div>
        </div>
      </section>
      <section className="py-14 md:py-20">
        <div className="wrap grid gap-12 lg:grid-cols-[0.8fr_1.2fr]">
          <SectionHead eyebrow="FAQ" title="Domande su questo servizio" />
          <FaqList faqs={s.faqs} id="service-faq" />
        </div>
      </section>
      <section className="border-t border-slate-100 py-10">
        <div className="wrap">
          <p className="text-sm font-semibold text-slate-600">Altri servizi</p>
          <div className="mt-4 flex flex-wrap gap-3">
            {related.map((r) => <Link key={r.slug} to={`/servizi/${r.slug}`} className="rounded-full border border-slate-200 px-4 py-2 text-sm font-medium transition-colors hover:border-slate-900">{r.name}</Link>)}
          </div>
        </div>
      </section>
      <CtaBand wa={s.wa} />
    </>
  );
}
